import type { ChannelValues } from '../animation/timeline';
import type { ExtractedEvents } from '../av/events';
import type { SceneEffects } from '../av/reactions';
import { contextRevision } from '../bridge/context';
import { PACKAGE_VERSION } from '../bridge/package';
import { CURRENT_SCHEMA_VERSION, type Animation, type AvSession, type Project, type Scene } from '../domain/schemas';
import type { AudioFeatures } from '../visualizer/analysis';
import { SERIES } from '../visualizer/analysis';
import { smoothedAt } from '../visualizer/features';
import type { CaptureResult } from './capture';
import { MAX_BRIEF_CHARS, MAX_REVIEW_DOCUMENT_BYTES, REVIEW_FORMAT, REVIEW_LIMITATIONS, REVIEW_VERSION, VisualReviewSchema, type VisualReview } from './schema';
import type { FrameGrid } from './timestamps';

export { MAX_BRIEF_CHARS, MAX_REVIEW_DOCUMENT_BYTES, REVIEW_FORMAT, REVIEW_LIMITATIONS, REVIEW_VERSION, VisualReviewSchema, type VisualReview };

/**
 * The `review.json` document of a visual review export (Phase 5C.1): what an external
 * assistant needs to read the frames next to it. It is built from the same frozen
 * snapshot the frames were rendered from, validated with a strict schema before it is
 * offered, and bounded in size.
 *
 * Privacy: it names nothing the user typed unless they opt in (`includeNames`): no project,
 * scene, layer, object, character, animation or session names, no on-screen text, no
 * image file names. It never contains audio, waveforms, file contents, credentials or
 * browser state; audio metadata (music events, measured levels) appears only for a sync
 * session, as numbers.
 */

export type ReviewSourceInfo = { kind: 'sync-session'; session: AvSession; animation: Animation | null } | { kind: 'animation'; animation: Animation };

export interface BuildReviewInput {
  project: Project;
  scene: Scene;
  source: ReviewSourceInfo;
  grid: FrameGrid;
  selection: 'even' | 'custom';
  /** How the source repeats: the animation's loop mode, 'session-loop' or 'none'. */
  loop: string;
  capture: CaptureResult;
  /** Optional per-frame SHA-256 hex digests, in frame order. */
  digests?: (string | undefined)[];
  brief?: { goal: string; constraints: string } | null;
  includeNames?: boolean;
  /** Sync sessions only. */
  events?: ExtractedEvents | null;
  features?: AudioFeatures | null;
  createdAt?: Date;
}

export class ReviewDocumentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReviewDocumentError';
  }
}

const round = (v: number, d = 3) => {
  const f = 10 ** d;
  return Math.round(v * f) / f;
};

function hash16(text: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x5bd1e995) >>> 0;
  }
  return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

function effectsOf(fx: SceneEffects | null): VisualReview['frames'][number]['effects'] {
  if (!fx) return null;
  return { flash: round(fx.flash?.alpha ?? 0), backgroundPulse: round(fx.background?.alpha ?? 0), shake: [round(fx.shake.x, 2), round(fx.shake.y, 2)] };
}

const roundValues = (v: ChannelValues): Record<string, number> => Object.fromEntries(Object.entries(v).filter(([, x]) => typeof x === 'number' && Number.isFinite(x)).map(([k, x]) => [k, round(x as number)]));

/** Builds and validates the document; throws `ReviewDocumentError` if it would be invalid or too large. */
export function buildReviewDocument(input: BuildReviewInput): VisualReview {
  const { project, scene, source, capture, grid } = input;
  const names = !!input.includeNames;
  const animation = source.kind === 'animation' ? source.animation : source.animation;
  const animated = new Map<string, string[]>();
  for (const t of animation?.tracks ?? []) animated.set(t.objectId, [...(animated.get(t.objectId) ?? []), t.channel]);
  const sceneSize = { width: scene.width, height: scene.height };
  const scale = Math.min(capture.width / scene.width, capture.height / scene.height);
  const session = source.kind === 'sync-session' ? source.session : null;
  const events = session ? (input.events ?? null) : null;
  const features = session ? (input.features ?? null) : null;

  const revision = contextRevision(project);
  const sourceTarget = session ? { kind: 'sync-session' as const, sessionId: session.id, animationId: session.animationId } : { kind: 'animation' as const, animationId: animation!.id };
  const reviewId = `vr_${hash16(JSON.stringify([revision, scene.id, sourceTarget, capture.width, capture.height, capture.frames.map((f) => round(f.seconds, 6))]))}`;

  const frames = capture.frames.map((f, i) => {
    let audio: VisualReview['frames'][number]['audio'] = null;
    if (events) {
      const nearby = events.events
        .filter((e) => Math.abs(e.seconds - f.seconds) <= 0.25 && e.category !== 'section')
        .sort((a, b) => Math.abs(a.seconds - f.seconds) - Math.abs(b.seconds - f.seconds) || a.id.localeCompare(b.id))
        .slice(0, 8)
        .map((e) => ({ category: e.category, offsetSec: round(e.seconds - f.seconds), velocity: round(e.velocity) }));
      audio = { nearbyEvents: nearby, levels: features ? { rms: round(smoothedAt(features, SERIES.rms, f.seconds, 0)), low: round(smoothedAt(features, SERIES.low, f.seconds, 0)), mid: round(smoothedAt(features, SERIES.mid, f.seconds, 0)), high: round(smoothedAt(features, SERIES.high, f.seconds, 0)) } : null };
    }
    return {
      id: f.id,
      file: reviewFileName(reviewId, i),
      seconds: round(f.seconds, 6),
      gridFrame: Math.floor(f.seconds * grid.fps + 1e-6),
      animationFrame: f.frame,
      bytes: f.bytes,
      ...(input.digests?.[i] ? { sha256: input.digests[i] } : {}),
      objects: Object.fromEntries(Object.entries(f.values).map(([id, v]) => [id, roundValues(v)])),
      effects: effectsOf(f.effects),
      audio,
    };
  });

  const objects = scene.objects.map((o) => {
    const base = {
      id: o.id,
      ...(names ? { name: o.name } : {}),
      type: o.type,
      layerId: o.layerId,
      box: [round(o.x, 2), round(o.y, 2), round(o.width, 2), round(o.height, 2)] as [number, number, number, number],
      rotation: round(o.rotation, 2),
      opacity: round(o.opacity),
      visible: o.visible,
      glow: o.glow ?? null,
      shadow: o.shadow ?? null,
      animatedChannels: animated.get(o.id) ?? [],
    };
    if ('style' in o) return { ...base, fill: o.style.fill, stroke: o.style.stroke, gradient: o.style.gradient ?? null, ...(o.type === 'rect' ? { cornerRadius: o.cornerRadius } : {}) };
    if (o.type === 'text') return { ...base, ...(names ? { text: o.text } : {}), textStyle: { fontSize: o.fontSize, color: o.color, bold: o.bold, align: o.align, fontFamily: o.fontFamily } };
    if (o.type === 'character') return { ...base, characterId: o.characterId, flipX: o.flipX };
    return { ...base, imageAssetId: o.assetId, fit: o.fit };
  });

  const usedCharacters = new Set(scene.objects.flatMap((o) => (o.type === 'character' ? [o.characterId] : [])));
  const characters = project.characters
    .filter((c) => usedCharacters.has(c.id))
    .map((c) => ({ id: c.id, ...(names ? { name: c.name } : {}), appearance: { skin: c.skin, head: c.head, eyes: c.eyes, hair: c.hair, clothing: c.clothing, accessories: c.accessories, proportions: c.proportions } }));

  const doc: VisualReview = {
    format: REVIEW_FORMAT,
    version: REVIEW_VERSION,
    reviewId,
    createdAt: (input.createdAt ?? new Date()).toISOString(),
    app: { name: 'SonicForge Studio', projectSchemaVersion: CURRENT_SCHEMA_VERSION, creationPackageVersion: PACKAGE_VERSION },
    target: { projectId: project.id, contextRevision: revision, sceneId: scene.id, ...(names ? { sceneName: scene.name } : {}), source: sourceTarget },
    brief: input.brief && (input.brief.goal.trim() || input.brief.constraints.trim()) ? { goal: input.brief.goal.trim().slice(0, MAX_BRIEF_CHARS), constraints: input.brief.constraints.trim().slice(0, MAX_BRIEF_CHARS) } : null,
    render: {
      renderer: 'video-export',
      width: capture.width,
      height: capture.height,
      sceneWidth: sceneSize.width,
      sceneHeight: sceneSize.height,
      scale: round(scale, 6),
      pixelRatio: 1,
      effectsQuality: 'full',
      framing: 'full-scene',
      camera: null,
      format: 'png',
      colorSpace: 'srgb',
    },
    timeline: { durationSec: round(grid.durationSec, 6), fps: grid.fps, loop: input.loop, selection: input.selection },
    frames,
    inventory: {
      scene: { width: scene.width, height: scene.height, background: scene.background, backgroundGradient: scene.gradient, hasBackgroundImage: !!scene.backgroundImage, look: scene.look ?? null },
      layers: scene.layers.map((l) => ({ id: l.id, ...(names ? { name: l.name } : {}), visible: l.visible, opacity: l.opacity ?? 1, blend: l.blend ?? 'normal', objectCount: scene.objects.filter((o) => o.layerId === l.id).length })),
      objects,
      characters,
      animation: animation
        ? { id: animation.id, ...(names ? { name: animation.name } : {}), fps: animation.fps, durationFrames: animation.durationFrames, seconds: round(animation.durationFrames / animation.fps, 6), loop: animation.loop, tracks: animation.tracks.map((t) => ({ objectId: t.objectId, channel: t.channel, keyframes: t.keyframes.length })) }
        : null,
      reactions: session ? session.mappings.map((m) => ({ id: m.id, reaction: m.reaction, categories: [...m.source.categories], objectId: m.target.kind === 'object' ? m.target.objectId : null, enabled: m.enabled })) : [],
      visualizer: session ? { layers: session.visualizer.layers.length, types: session.visualizer.layers.map((l) => l.type) } : null,
    },
    audio: events
      ? {
          bpm: round(events.bpm, 3),
          durationSec: round(events.totalSeconds, 3),
          eventCounts: Object.fromEntries([...events.byCategory.entries()].map(([k, v]) => [k, v.length]).sort(([a], [b]) => String(a).localeCompare(String(b)))),
          analysis: features ? 'measured' : 'not-available',
        }
      : null,
    limitations: [...REVIEW_LIMITATIONS],
    privacy: {
      namesIncluded: names,
      excluded: [
        'project name and description',
        ...(names ? [] : ['scene, layer, object, character, animation and session names', 'on-screen text']),
        'image files and their names',
        'audio, recordings and waveforms',
        'music notes and arrangement',
        'other scenes, settings and browser data',
      ],
    },
  };
  const parsed = VisualReviewSchema.safeParse(doc);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    throw new ReviewDocumentError(`The review document is invalid (${issue?.path.join('.') || 'document'}: ${issue?.message ?? 'invalid'}).`);
  }
  const bytes = new TextEncoder().encode(JSON.stringify(parsed.data, null, 2)).length;
  if (bytes > MAX_REVIEW_DOCUMENT_BYTES) throw new ReviewDocumentError(`review.json would be ${Math.ceil(bytes / 1024)} KB, over the ${MAX_REVIEW_DOCUMENT_BYTES / 1024} KB limit. Choose fewer frames.`);
  return parsed.data;
}

/** File name of frame `index` (0-based) of a review: unique per review, so downloads never collide. */
export const reviewFileName = (reviewId: string, index: number): string => `review-${reviewId.slice(3, 11)}-frame-${String(index + 1).padStart(2, '0')}.png`;
/** File name of the review document itself. */
export const reviewDocumentName = (reviewId: string): string => `review-${reviewId.slice(3, 11)}.json`;

/** SHA-256 hex of a Blob, or undefined where Web Crypto is unavailable (insecure context). */
export async function sha256Hex(blob: Blob): Promise<string | undefined> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle || typeof blob.arrayBuffer !== 'function') return undefined;
  try {
    const digest = await subtle.digest('SHA-256', await blob.arrayBuffer());
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}
